import React, { Component } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { Modal, Button, Icon, Label, Input, Dimmer, Loader } from 'semantic-ui-react';
import RequestFormTabMenu from './tabs/request-form-tab-menu';
import { decamelCase } from 'utils/event-utils';
import RequestFormChat from './request-form-chat';
import { RequestDto, ChangeRequestDto } from 'stores/requests/requests-store';
import RequestEditButton from './buttons/request-edit-button';
import RequestAcceptButton from './buttons/request-accept-button';
import RequestCancelButton from './buttons/request-cancel/request-cancel-button';
import RequestSendButton from './buttons/request-send-button';
import RequestReopenButton from './buttons/request-reopen-button';
import RequestRejectButton from './buttons/request-reject/request-reject-button';
import { RequestFormStore, RequestFormViewModel, buildRequestFormViewModel } from 'stores/requests/request-form-store';
import { connect } from 'redux-scaffolding-ts';
import '../../shared/events-and-requests/events-requests-form.less';
import { History } from 'history';
import { Message } from 'stores/types';
import { isExtendedWorkflow } from 'utils/event-type-utils';
import { RequestChatStore } from 'stores/requests/requests-chat-stores';
import { ToastComponent } from 'site/pages/landing-pages/util/toast-component';

export type RequestFormActions = 'close' | 'cancel' | 'edit' | 'accept' | 'reject' | 'send' | 'reopen' | 'save';

export interface RequestFormProps extends WithTranslation {
  onClose?: (request?: RequestDto, action?: RequestFormActions) => void;
  request: RequestFormViewModel;
  requestFormStore?: RequestFormStore;
  requestChatStore?: RequestChatStore;
  history?: History;
  fromEventTab: boolean;
}

export interface RequestFormState {
  readOnly: boolean;
  loading: boolean;
  errors: Message[];
  saveDone: boolean;
}

@connect(['requestFormStore', RequestFormStore], ['requestChatStore', RequestChatStore])
class RequestForm extends Component<RequestFormProps, RequestFormState> {
  state: RequestFormState = {
    readOnly: true,
    loading: false,
    errors: [],
    saveDone: false
  };

  constructor(props: Readonly<RequestFormProps>) {
    super(props);
    this.props.requestFormStore.change({ ...this.props.request });
  }

  componentWillUnmount() {
    this.props.requestFormStore.reset();
    this.props.requestChatStore.reset();
  }

  private handleOnCloseModal = (request?: RequestDto, actionName?: RequestFormActions) => {
    if (this.props.onClose) {
      const action = this.replaceActionOnClose(actionName);
      this.props.onClose(request, action);
    }

    if (this.props.history) this.props.history.replace('/requests');
  };

  private replaceActionOnClose = (actionName?: RequestFormActions): RequestFormActions => {
    let action: RequestFormActions = actionName;
    if (action === 'close' && this.state.saveDone) {
      action = 'save';
    }
    return action;
  };

  private changeToEventCreated = (request: RequestDto = null) => {
    if (this.props.history && request && request.eventCreatedId && !isExtendedWorkflow(request.eventType.eventTypeCategory))
      this.props.history.replace(`/events/${request.eventCreatedId}`);
    else {
      this.setState({ loading: false });
      this.handleOnCloseModal(request);
    }
  };

  private handleOnRequestChange = (property: keyof ChangeRequestDto, value: any) => {
    let changes = {};
    changes[property] = value;

    this.props.requestFormStore.change(changes);
  };

  private handleOnSave = () => {
    this.setState({ loading: true });
    this.props.requestFormStore
      .updateRequest()
      .then(result => {
        if (result?.isSuccess) {
          this.setState({ readOnly: result.isSuccess, loading: false, saveDone: true });
          ToastComponent({ text: 'Request saved successfully!', type: 'success-toast' });
        } else this.setState({ loading: false });
      })
      .catch(err => {
        console.error(err);
        (this.props.requestFormStore.state.result?.messages || []).forEach(msg =>
          ToastComponent({ text: msg.body, type: 'warning-toast' })
        );
        this.setState({ loading: false });
      });
  };

  private handleShowToastReopened = (request: RequestDto) => {
    const { t, requestFormStore } = this.props;
    this.setState({ loading: false });
    ToastComponent({ type: 'success-toast', text: t('Form successfully reopened!') });
    requestFormStore.change(buildRequestFormViewModel(request));
  };

  render() {
    const { requestFormStore, t, fromEventTab } = this.props;
    const request = requestFormStore.state.item;
    const { readOnly, loading, errors } = this.state;
    if (!request) return null;

    return (
      <>
        <Modal
          className="planit-generic-form__modal events-form__modal"
          open
          closeOnEscape={true}
          onClose={() => this.handleOnCloseModal(null, 'close')}
          closeOnDimmerClick={false}
        >
          <Dimmer active={loading} style={{ background: 'rgba(0, 0, 0, 0.4)' }}>
            <Loader indeterminate>{t('Loading')}</Loader>
          </Dimmer>
          <div className="form__modal__left-container">
            <div className="form__modal__close-menu-left-container">
              <div className="form__header-top-container">
                <Button className="form__modal__close-btn" icon onClick={() => this.handleOnCloseModal(null, 'close')}>
                  <Icon name="times" />
                </Button>

                <div className="form__header-title-status">
                  <div className="flex-start-center">
                    <Input className="form__header__title-input__friendlyId" readOnly value={`${request.friendlyId}   - `} />
                    <Input
                      className="form__header__title-input"
                      readOnly={readOnly}
                      value={request.title}
                      onChange={(_, { value }) => this.handleOnRequestChange('title', value)}
                    />
                  </div>

                  <Label floated="right" className="form__header__status-tag" circular compact="true">
                    {request.statusDescription || decamelCase(request.status.toString())}
                  </Label>
                </div>
              </div>

              <RequestFormTabMenu extraErrors={errors} readOnly={readOnly} fromEventTab={fromEventTab} />

              <Modal.Actions className="form__action-buttons-wrapper">
                <RequestCancelButton
                  readOnly={this.state.readOnly}
                  requestId={request.id}
                  isGea={request.isGEA}
                  isGma={request.isGMA}
                  requestCreatorRoles={request.rolesThatCreateRequests}
                  requestStatus={request.status}
                  onSuccess={() => this.handleOnCloseModal(null, 'cancel')}
                />

                <div className="form__align-right-btns">
                  <RequestEditButton
                    requestId={request.id}
                    requestStatus={request.status}
                    requestCreatorRoles={request.rolesThatCreateRequests}
                    isGea={request.isGEA}
                    isGma={request.isGMA}
                    extendedStatusValidation={request.extendedStatusValidation}
                    isOnEditMode={!this.state.readOnly}
                    onEdit={() => this.setState({ readOnly: false })}
                    onSave={this.handleOnSave}
                  />

                  <RequestRejectButton
                    readOnly={this.state.readOnly}
                    requestId={request.id}
                    isGea={request.isGEA}
                    isGma={request.isGMA}
                    extendedStatusValidation={request.extendedStatusValidation}
                    requestValidatorRoles={request.rolesThatValidateRequests}
                    requestStatus={request.status}
                    onSuccess={this.handleOnCloseModal}
                  />

                  <RequestAcceptButton
                    readOnly={this.state.readOnly}
                    requestId={request.id}
                    onBeginAccept={() => this.setState({ loading: true })}
                    requestValidatorRoles={request.rolesThatValidateRequests}
                    isGea={request.isGEA}
                    isGma={request.isGMA}
                    extendedStatusValidation={request.extendedStatusValidation}
                    requestStatus={request.status}
                    onSuccess={this.changeToEventCreated}
                    onError={extraErrors => this.setState({ loading: false, errors: extraErrors })}
                  />

                  <RequestSendButton
                    readOnly={this.state.readOnly}
                    requestId={request.id}
                    onBeginSend={() => this.setState({ loading: true })}
                    requestStatus={request.status}
                    onSuccess={this.handleOnCloseModal}
                    onError={() => this.setState({ loading: false })}
                    requestCreatorRoles={request.rolesThatCreateRequests}
                  />

                  <RequestReopenButton
                    readOnly={this.state.readOnly}
                    requestId={request.id}
                    onBeginReOpen={() => this.setState({ loading: true })}
                    requestStatus={request.status}
                    onSuccess={this.handleShowToastReopened}
                    onError={() => this.setState({ loading: false })}
                    requestCreatorRoles={request.rolesThatCreateRequests}
                  />
                </div>
              </Modal.Actions>
            </div>
          </div>

          <RequestFormChat requestId={request.id} status={request.status} />
        </Modal>
      </>
    );
  }
}

export default withTranslation()(RequestForm);
